/**
 * Profit by product — rolls the (customer × product) baseline rows up to one row
 * per vendor product, summing seats and MRR across all customers holding it.
 */

import React, { useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import type { ComputedProfitRow } from '../../utils/profitCompute';
import { fmtMoney, fmtPct } from '../../utils/profitCompute';
import { headCellSx, bodyCellSx } from './profitFlags';

interface ProductGroup {
  vendorProductId: string;
  productName: string | null;
  vendorName: string | null;
  sku: string | null;
  customers: number;
  seats: number;
  billedSeats: number;
  mrrBuy: number;
  mrrSell: number;
  mrrProfit: number;
  knownSell: number; // sum of mrrSell over rows with both sides known (for margin)
  knownProfit: number;
}

function groupByProduct(rows: ComputedProfitRow[]): ProductGroup[] {
  const map = new Map<string, ProductGroup>();
  for (const r of rows) {
    let g = map.get(r.vendorProductId);
    if (!g) {
      g = {
        vendorProductId: r.vendorProductId,
        productName: r.productName,
        vendorName: r.vendorName,
        sku: r.sku,
        customers: 0,
        seats: 0,
        billedSeats: 0,
        mrrBuy: 0,
        mrrSell: 0,
        mrrProfit: 0,
        knownSell: 0,
        knownProfit: 0,
      };
      map.set(r.vendorProductId, g);
    }
    g.customers += 1;
    g.seats += r.seats;
    g.billedSeats += r.billedSeats;
    if (r.mrrBuy !== null && r.mrrSell !== null && r.mrrProfit !== null) {
      g.mrrBuy += r.mrrBuy;
      g.mrrSell += r.mrrSell;
      g.mrrProfit += r.mrrProfit;
      g.knownSell += r.mrrSell;
      g.knownProfit += r.mrrProfit;
    }
  }
  return Array.from(map.values()).sort((a, b) => b.mrrProfit - a.mrrProfit);
}

export const ProfitByProductTable: React.FC<{ rows: ComputedProfitRow[] }> = ({ rows }) => {
  const groups = useMemo(() => groupByProduct(rows), [rows]);

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={headCellSx}>Product</TableCell>
            <TableCell sx={headCellSx}>Vendor</TableCell>
            <TableCell sx={headCellSx}>SKU</TableCell>
            <TableCell sx={headCellSx} align="right">Customers</TableCell>
            <TableCell sx={headCellSx} align="right">Seats</TableCell>
            <TableCell sx={headCellSx} align="right">MRR Buy</TableCell>
            <TableCell sx={headCellSx} align="right">MRR Sell</TableCell>
            <TableCell sx={headCellSx} align="right">MRR Profit</TableCell>
            <TableCell sx={headCellSx} align="right">Margin</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map((g, i) => (
            <TableRow
              key={g.vendorProductId}
              sx={{ backgroundColor: i % 2 === 1 ? 'action.hover' : 'transparent' }}
            >
              <TableCell sx={bodyCellSx}>{g.productName ?? '—'}</TableCell>
              <TableCell sx={bodyCellSx}>{g.vendorName ?? '—'}</TableCell>
              <TableCell sx={bodyCellSx}>{g.sku ?? '—'}</TableCell>
              <TableCell sx={bodyCellSx} align="right">{g.customers}</TableCell>
              <TableCell sx={bodyCellSx} align="right">{g.seats}</TableCell>
              <TableCell sx={bodyCellSx} align="right">{fmtMoney(g.mrrBuy)}</TableCell>
              <TableCell sx={bodyCellSx} align="right">{fmtMoney(g.mrrSell)}</TableCell>
              <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }} align="right">{fmtMoney(g.mrrProfit)}</TableCell>
              <TableCell sx={bodyCellSx} align="right">
                {fmtPct(g.knownSell ? g.knownProfit / g.knownSell : null)}
              </TableCell>
            </TableRow>
          ))}
          {!groups.length && (
            <TableRow>
              <TableCell sx={bodyCellSx} colSpan={9} align="center">No data.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
